import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-a-new-start",
  title: "A prayer for a new start — in January or in March",
  h1: "A prayer for a new start",
  description:
    "A prayer for beginning again after the last attempt failed, four passages on new things, and why the date you start does not matter as much as it feels.",
  targets: [
    "prayer for a new start",
    "prayer for a fresh start",
    "new year prayer",
    "prayer for starting over",
  ],
  published: "2026-09-03",
  standfirst:
    "Everybody starts on the first of January. Almost nobody starts on the fourteenth of March, which is why the fourteenth of March is the more serious date.",
  takeaways: [
    "Start today rather than on the next round number. The wait for a good starting date is the most common way a resolution dies before it begins.",
    "Philippians 3:13 puts forgetting and stretching forward in the same breath — the letting go is what makes the reaching possible, not a separate step.",
    "A new start does not require the last one to have been a failure. Most durable change is a resumption, not a fresh beginning.",
  ],
  when: [
    "On the first of January, with everyone else.",
    "In the third week of January, when the first attempt has already gone.",
    "The morning after breaking something you had kept for a month.",
    "Any ordinary Tuesday, which is the best available date.",
  ],
  prayer: [
    "God, I am starting again. It is not the first time and we both know roughly how many times it has been.",
    "I am not going to promise anything this time. Promising is what I did before and the promise was the least useful part of it.",
    "Take the shame off the last attempt. Not the memory of it, which is useful — the shame, which only makes the next attempt heavier to lift.",
    "Give me a small thing to do today, and let me do it badly rather than not at all.",
    "And when I miss a day, which I will, let missing one day not become the reason I stop for a month.",
  ],
  short:
    "God, I am starting again. Something small today, done badly rather than not at all.",
  honest: [
    "This prayer does not erase what happened. 2 Corinthians 5:17 is about a change of standing before God, not about consequences going away, and treating it as a clean slate in the practical sense sets people up badly.",
    "It also does not supply motivation. Motivation is not what carries anything past week three; an arrangement is, and no amount of praying substitutes for deciding in advance what happens on the bad day.",
    "And starting again does not require a dramatic story. Most change that lasts is unremarkable, undated, and would not make a testimony.",
  ],
  verses: [
    {
      verse: {
        text: "Don't remember the former things, and don't consider the things of old. Behold, I will do a new thing. It springs out now. Don't you know it? I will even make a way in the wilderness, and rivers in the desert.",
        ref: "Isaiah 43:18–19",
      },
      note: "Said to exiles, and the former things being dismissed are not their failures but their greatest hits — the exodus, the sea, the army drowned. Do not keep looking at the last rescue; there is another one, and it will not look the same. That is a considerably more interesting instruction than forget your past mistakes.",
    },
    {
      verse: {
        text: "Brothers, I don't regard myself as yet having taken hold, but one thing I do: forgetting the things which are behind and stretching forward to the things which are before, I press on toward the goal for the prize of the high calling of God in Christ Jesus.",
        ref: "Philippians 3:13–14",
      },
      note: "A runner's image, and the forgetting is mechanical rather than emotional — you cannot lean forward while looking over your shoulder. Paul begins by saying he has not arrived, which is worth noticing on a page about starting again: the man being quoted is himself mid-attempt.",
    },
    {
      verse: {
        text: "Therefore if anyone is in Christ, he is a new creation. The old things have passed away. Behold, all things have become new.",
        ref: "2 Corinthians 5:17",
      },
      note: "A statement about standing, not about circumstances, and the distinction matters on a day when somebody is hoping their debts or their habits have been reset. The chapter is arguing that how a person is regarded has changed permanently — which is the ground you start again from, rather than the result.",
    },
    {
      verse: {
        text: "It is because of Yahweh's loving kindnesses that we are not consumed, because his mercies don't fail. They are new every morning. Great is your faithfulness.",
        ref: "Lamentations 3:22–23",
      },
      note: "The strongest argument available against needing the first of January. If the mercies are new every morning, then every morning is a starting date, and the ones in the middle of February count the same as the ones with fireworks attached.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The date is not the problem",
      id: "the-date-is-not-the-problem",
    },
    {
      type: "p",
      text: "Waiting for Monday, the first, the new term, the end of the trip — the wait feels like preparation and functions as delay. By the time the date arrives, the decision has been carrying so much expectation that the first failure ends it.",
    },
    {
      type: "p",
      text: "Lamentations offers a better arrangement: the mercies are new every morning, which makes every morning a first of January and none of them special enough to be ruined.",
    },
    {
      type: "h2",
      text: "Decide the bad day in advance",
      id: "decide-the-bad-day-in-advance",
    },
    {
      type: "p",
      text: "Almost every attempt at anything fails at the same point: not the first missed day, but the decision made on the second one that the whole thing is now void. That decision is predictable, so it can be pre-empted.",
    },
    {
      type: "note",
      text: 'Write down now what you will do on the day you miss. "Start again the next morning, no discussion" is a sufficient plan and it is the whole difference.',
    },
    {
      type: "h2",
      text: "Small enough to do badly",
      id: "small-enough-to-do-badly",
    },
    {
      type: "p",
      text: "Resolutions are usually set at the size of the intention rather than the size of a Tuesday. A {reading plan} for the year fails in February because it was designed for a version of you who has an hour, and the version of you that exists has eleven minutes.",
      links: {
        "reading plan": "/blog/bible-in-a-year-on-your-phone",
      },
    },
    {
      type: "p",
      text: "Set it small enough that you can do it badly on a bad day. The bad-day version is the one that decides whether any of it survives.",
    },
  ],
  faq: [
    {
      q: "What can I pray at the start of a new year?",
      a: 'Ask for something small and specific for today rather than for the year: "God, I am starting again — something small today, done badly rather than not at all." The year is not the unit anything is actually done in.',
    },
    {
      q: "Which Bible verse is about new beginnings?",
      a: "Isaiah 43:18–19 for the new thing that springs out now, and Lamentations 3:22–23 for the reason you do not have to wait for January: the mercies are described as new every morning.",
    },
    {
      q: "How do I start again after failing repeatedly?",
      a: "Take the shame off the last attempt and keep the information. Then decide in advance what happens on the day you miss — that decision, not motivation, is what most attempts actually die of.",
    },
  ],
  relatedPrayers: ["prayer-for-self-control", "prayer-for-hope"],
  related: ["bible-in-a-year-on-your-phone", "dopamine-detox-christian"],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-self-control"],
  relatedPassages: ["jeremiah-29-11", "romans-12-2"],
};
